import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocumentSaveOptions } from '@/lib/meetingPage.ts';

/* Long enough that a sentence is one save, short enough to feel instant. */
const SAVE_AFTER_MS = 600;

interface PendingSave<T> {
  next: T;
  save: (next: T, options: DocumentSaveOptions) => void;
}

/**
 * A document the user types into: held locally and saved on a short debounce,
 * so a keystroke never waits on the network and the caret never jumps. A newer
 * copy from the server replaces it only while nothing typed is unsaved. Leaving
 * the editor or the page saves what is still waiting at once.
 */
export function useAutosavedDocument<T>(
  fromServer: T | null,
  onSave: (next: T, options: DocumentSaveOptions) => void,
): [T | null, (next: T) => void] {
  const [document, setDocument] = useState<T | null>(fromServer);
  const [unsaved, setUnsaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<PendingSave<T> | null>(null);

  const stamp = fromServer ? JSON.stringify(fromServer) : null;
  const [seenStamp, setSeenStamp] = useState(stamp);
  if (stamp !== seenStamp) {
    setSeenStamp(stamp);
    if (!unsaved) setDocument(fromServer);
  }

  /* Sends what is waiting, once: the timer, a leave and an unmount never save it twice. */
  const flush = useCallback((options: DocumentSaveOptions) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const waiting = pending.current;
    pending.current = null;
    waiting?.save(waiting.next, options);
  }, []);

  useEffect(() => {
    const leavePage = () => {
      flush({ keepalive: true });
      setUnsaved(false);
    };
    window.addEventListener('pagehide', leavePage);
    return () => {
      window.removeEventListener('pagehide', leavePage);
      flush({ keepalive: true });
    };
  }, [flush]);

  const write = (next: T) => {
    setDocument(next);
    setUnsaved(true);
    pending.current = { next, save: onSave };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setUnsaved(false);
      flush({ keepalive: false });
    }, SAVE_AFTER_MS);
  };

  return [document, write];
}
